import React from 'react';

import { createBEMClasses } from '@/utils/classname';

import './Paragraph.css';

const { block } = createBEMClasses('paragraph');

export const PARAGRAPH_SIZES = ['50', '100', '200', '300', '400'] as const;
export const PARAGRAPH_WEIGHTS = ['light', 'regular', 'medium', 'bold', 'heavy'] as const;
export const PARAGRAPH_COLORS = [
  'primary',
  'secondary',
  'disabled',
  'success',
  'warning',
  'danger',
  'neutral',
] as const;

type ParagraphSize = (typeof PARAGRAPH_SIZES)[number];
type ParagraphWeight = (typeof PARAGRAPH_WEIGHTS)[number];
type ParagraphColor = (typeof PARAGRAPH_COLORS)[number];

type ParagraphBaseProps = React.AriaAttributes & React.HTMLAttributes<HTMLParagraphElement>;

export interface ParagraphProps extends ParagraphBaseProps {
  size?: ParagraphSize;
  mobileSize?: ParagraphSize;
  weight?: ParagraphWeight;
  color?: ParagraphColor;
  children: React.ReactNode;
  nowrap?: boolean;
  ellipsis?: boolean;
  align?: 'left' | 'center' | 'right' | 'start' | 'end';
  textTransform?: 'capitalize' | 'uppercase' | 'lowercase' | 'none';
  whiteSpace?: 'normal' | 'nowrap' | 'pre-wrap' | 'pre-line';
  wordBreak?: 'normal' | 'break-all' | 'break-word';
  flex?: React.CSSProperties['flex'];
  title?: string;
  id?: string;
  ref?: React.Ref<HTMLParagraphElement>;
}

const Paragraph = ({
  size = '100',
  mobileSize,
  weight = 'regular',
  color,
  children,
  className,
  nowrap = false,
  ellipsis = false,
  align,
  textTransform,
  whiteSpace,
  wordBreak,
  flex,
  style,
  title,
  id,
  ref,
  ...rest
}: ParagraphProps) => {
  const commonProps = {
    ...rest,
    className: block(
      { size, weight, ...(color && { color }), ...(mobileSize && { 'mobile-size': mobileSize }) },
      className
    ),
    style: {
      whiteSpace: whiteSpace ?? (nowrap ? 'nowrap' : undefined),
      wordBreak,
      textOverflow: ellipsis ? 'ellipsis' : undefined,
      overflow: ellipsis ? 'hidden' : undefined,
      maxWidth: ellipsis ? '100%' : undefined,
      textAlign: align,
      textTransform,
      flex,
      ...style,
    },
    id,
    title,
  };

  return (
    <p ref={ref} {...commonProps}>
      {children}
    </p>
  );
};

export default Paragraph;
